import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReportCard from 'component/reports/ReportCard';
import AiGenerateReportField from 'component/reports/AiGenerateReportField';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { reportService } from '../../services/reportService';
import Transport from "../../assets/images/report_img.jpg";
import { proxiedLogoUrl } from "utils/logoUrl";
import { translateSectorLabel } from "utils/sectorLabel";

const PLACEHOLDER_IMG = "https://news-demo.dataflock.de/api/news/placeholder";
const SECTORS_PAGE_SIZE = 24;
const toPercentScore = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const num = Number(value);
  if (!Number.isFinite(num)) return null;
  return Math.round(num <= 1 ? num * 100 : num);
};
const normalizeSectors = (value) => {
  const input = Array.isArray(value) ? value : (value ? [value] : []);
  return input
    .map((sector) => {
      if (typeof sector === 'string') return sector;
      if (sector && typeof sector === 'object') return sector.name || sector.slug || '';
      return '';
    })
    .filter(Boolean);
};

const mapReport = (r = {}) => ({
  id: r.id ?? r.report_id ?? r._id,
  title: r.title || r.name || '',
  summary: r.summary || r.description || '',
  image_url: r.hero_image_url || r.image_url || r.cover_image || PLACEHOLDER_IMG,
  match_rate: r.match_rate ?? r.score ?? null,
  sectors: normalizeSectors(r.sectors || (r.sector ? [r.sector] : [])),
  recent_at: r.created_at || r.published_at || r.updated_at,
  reading_time_minutes: r.reading_time_minutes || r.read_time_minutes || r.reading_minutes || null,
});

const mapCustomReport = (r = {}) => ({
  id: r.id,
  title: r.title || r.prompt || '',
  summary: r.ai_summary || '',
  image_url: r.hero_image_url || PLACEHOLDER_IMG,
  match_rate: toPercentScore(r.match_score ?? r.match_rate ?? r.score),
  sectors: [],
  recent_at: r.created_at || r.updated_at,
  reading_time_minutes: r.reading_time_minutes || r.read_time_minutes || r.reading_minutes || r?.metadata_json?.read_time_minutes || null,
  status: r.status,
  progress: (r.metadata_json && r.metadata_json.progress) || (r.status === 'completed' ? 100 : 10),
  eta_hint: r?.metadata_json?.eta_hint || "2-5 minutes",
  remaining_seconds: r?.metadata_json?.remaining_seconds,
  error: r.error || '',
  prompt_key: `${(r.prompt || "").trim().toLowerCase()}`,
});
const customReportsSignature = (reports) =>
  reports
    .map((r) => `${r.id}:${r.status}:${r.progress}:${r.match_rate}:${r.recent_at || ''}`)
    .join('|');
const dedupeGeneratingReports = (reports) => {
  const byKey = new Map();
  for (const report of reports || []) {
    const key = report.status === 'generating' ? `prompt:${report.prompt_key || ""}` : `id:${report.id}`;
    if (!key) continue;
    const existing = byKey.get(key);
    if (!existing) {
      byKey.set(key, report);
      continue;
    }
    const existingTs = new Date(existing.recent_at || 0).getTime();
    const reportTs = new Date(report.recent_at || 0).getTime();
    if (reportTs > existingTs) byKey.set(key, report);
  }
  return Array.from(byKey.values()).sort(
    (a, b) => new Date(b.recent_at || 0).getTime() - new Date(a.recent_at || 0).getTime()
  );
};

const ReportList = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage || i18n.language;
  const [reports, setReports] = useState([]);
  const [recentReports, setRecentReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingRecents, setLoadingRecents] = useState(false);
  const [recentError, setRecentError] = useState(null);
  const [error, setError] = useState(null);
  const [customReports, setCustomReports] = useState([]);
  const [loadingCustom, setLoadingCustom] = useState(false);
  const [retryingId, setRetryingId] = useState(null);
  const [sources, setSources] = useState([]);
  const [loadingSources, setLoadingSources] = useState(false);
  const [sectors, setSectors] = useState([]);
  const [loadingSectors, setLoadingSectors] = useState(false);
  const [visibleSectorsCount, setVisibleSectorsCount] = useState(SECTORS_PAGE_SIZE);
  const [fields, setFields] = useState([]);
  const [loadingFields, setLoadingFields] = useState(false);
  const customReportsSigRef = useRef('');
  const customReportsLoadedRef = useRef(false);
  const navigate = useNavigate();

  useEffect(() => {
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await reportService.listMatchingReports({ limit: 12 });
        const arr = Array.isArray(data) ? data : [];
        setReports(arr.map(mapReport));
      } catch (e) {
        setError(e?.message || t('reports.error.load'));
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [t]);

  useEffect(() => {
    const loadRecents = async () => {
      setLoadingRecents(true);
      setRecentError(null);
      try {
        const data = await reportService.listRecentReports({ limit: 6 });
        const arr = Array.isArray(data) ? data : [];
        setRecentReports(arr.map(mapReport));
      } catch (e) {
        setRecentReports([]);
        setRecentError(e?.message || t("reports.error.loadRecent"));
      } finally {
        setLoadingRecents(false);
      }
    };
    loadRecents();
  }, [t]);

  useEffect(() => {
    const loadSectors = async () => {
      setLoadingSectors(true);
      try {
        const data = await reportService.listReportSectors({ limit: 200 });
        setSectors(Array.isArray(data) ? data : []);
      } catch (e) {
        setSectors([]);
      } finally {
        setLoadingSectors(false);
      }
    };
    loadSectors();
  }, []);

  useEffect(() => {
    const loadFields = async () => {
      setLoadingFields(true);
      try {
        const data = await reportService.listReportFields({ limit: 4 });
        setFields(Array.isArray(data) ? data : []);
      } catch (e) {
        setFields([]);
      } finally {
        setLoadingFields(false);
      }
    };
    loadFields();
  }, []);

  useEffect(() => {
    const loadSources = async () => {
      setLoadingSources(true);
      try {
        const data = await reportService.listReportSources({ limit: 8 });
        setSources(Array.isArray(data) ? data : []);
      } catch (e) {
        setSources([]);
      } finally {
        setLoadingSources(false);
      }
    };
    loadSources();
  }, []);

  useEffect(() => {
    let interval;
    const loadCustom = async () => {
      if (!customReportsLoadedRef.current) setLoadingCustom(true);
      try {
        const data = await reportService.listCustomReports();
        const arr = Array.isArray(data) ? dedupeGeneratingReports(data.map(mapCustomReport)) : [];
        const sig = customReportsSignature(arr);
        if (sig !== customReportsSigRef.current) {
          customReportsSigRef.current = sig;
          setCustomReports(arr);
        }
      } catch (e) {
        // swallow errors for polling
      } finally {
        if (!customReportsLoadedRef.current) {
          setLoadingCustom(false);
          customReportsLoadedRef.current = true;
        }
      }
    };
    loadCustom();
    interval = setInterval(loadCustom, 15000);
    return () => clearInterval(interval);
  }, []);

  const handleCustomCreated = (report) => {
    if (!report) return;
    const mapped = mapCustomReport(report);
    setCustomReports((prev) => {
      const existing = prev.find((r) => r.id === mapped.id);
      if (existing) {
        return prev.map((r) => (r.id === mapped.id ? mapped : r));
      }
      return dedupeGeneratingReports([mapped, ...prev]);
    });
  };

  const handleRetry = async (reportId) => {
    if (!reportId) return;
    setRetryingId(reportId);
    try {
      await reportService.retryCustomReport(reportId);
      const data = await reportService.listCustomReports();
      const arr = Array.isArray(data) ? dedupeGeneratingReports(data.map(mapCustomReport)) : [];
      const sig = customReportsSignature(arr);
      customReportsSigRef.current = sig;
      setCustomReports(arr);
    } catch (e) {
      setError(e?.message || t("reports.error.load"));
    } finally {
      setRetryingId(null);
    }
  };

  const suggested = useMemo(() => reports.slice(0, 12), [reports]);
  const visibleSectors = useMemo(
    () => sectors.slice(0, visibleSectorsCount),
    [sectors, visibleSectorsCount]
  );

  return (
    <div className='main__content__box pt-0 '>
      <div className="container-xxl ms-0">
        {/* generate with ai  */}
        <div className="py-5">
          <AiGenerateReportField onCreated={handleCustomCreated} />
        </div>

        {(loadingRecents || recentReports.length > 0) && (
          <div className='content_wrap'>
            <h4 className='text-white fw-semibold mb-4'>{t("reports.recentReports")}</h4>
            {error && <div className="text-danger mb-3">{error}</div>}
            {(loading || loadingRecents) && <div className="text-muted mb-3">{t("reports.loading")}</div>}
            {recentError && !loadingRecents && <div className="text-danger mb-3">{recentError}</div>}
            <div className="row g-4">
              {recentReports.map((report, index) => (
                <div className="col-md-6 col-lg-4" key={index}>
                  <ReportCard
                    value={index + 1}
                    title={report.title || t("reports.untitled")}
                    summary={report.summary}
                    _id={report.id}
                    progress={report.match_rate}
                    image_url={report.image_url}
                    sectors={report.sectors}
                    created_at={report.recent_at}
                    reading_time_minutes={report.reading_time_minutes}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {customReports.length > 0 && (
          <div className={`content_wrap ${(loadingRecents || recentReports.length > 0) ? 'mt-5' : ''}`}>
            <div className="d-flex justify-content-between align-items-center mb-4">
            <h4 className='text-white fw-semibold mb-0'>{t("reports.myReports")}</h4>
              <button
                type="button"
                className="btn btn-outline-light btn-sm px-3"
                onClick={() => navigate("/reports/library")}
              >
                {t("reports.myLibrary")}
              </button>
            </div>
            {loadingCustom && <div className=""></div>}
            <div className="row g-4">
              {customReports.map((report, index) => {
                const generating = report.status === 'generating';
                const failed = report.status === 'failed';
                const progress = report.progress || 0;
                const remaining =
                  Number.isFinite(Number(report.remaining_seconds)) && Number(report.remaining_seconds) > 0
                    ? `~${Math.ceil(Number(report.remaining_seconds) / 60)} min left`
                    : report.eta_hint;
                return (
                  <div className="col-md-6 col-lg-4" key={report.id || index}>
                    <div className="position-relative reportCard_wrapper">
                      <ReportCard
                        value={index + 1}
                        title={report.title || t("reports.customReport")}
                        summary={
                          report.summary ||
                          (failed ? t("reports.customReportGenerationFailed", "Generation failed") : t("reports.customReportGenerating"))
                        }
                        _id={report.status === 'completed' ? report.id : null}
                        reportType="custom"
                        progress={generating ? progress : report.match_rate}
                        image_url={report.image_url}
                        sectors={report.sectors}
                        created_at={report.recent_at}
                        reading_time_minutes={report.reading_time_minutes}
                      />
                      {generating && (
                        <div className="reportCard_overlay d-flex flex-column justify-content-center align-items-center">
                          <div className="progress w-75">
                            <div
                              className="progress-bar"
                              role="progressbar"
                              style={{ width: `${Math.min(progress, 100)}%` }}
                              aria-valuenow={progress}
                              aria-valuemin="0"
                              aria-valuemax="100"
                            />
                          </div>
                          <span className="text-white small mt-2">{t("reports.generating")} ({remaining})</span>
                        </div>
                      )}
                      {failed && (
                        <div className="reportCard_overlay d-flex flex-column justify-content-center align-items-center">
                          <span className="text-danger fw-semibold text-center px-3">
                            {report.error || t("reports.customReportGenerationFailed", "Generation failed")}
                          </span>
                          <button
                            type="button"
                            className="btn btn-danger btn-sm mt-2"
                            disabled={retryingId === report.id}
                            onClick={() => handleRetry(report.id)}
                          >
                            {retryingId === report.id
                              ? t("reports.retrying", "Retrying...")
                              : t("reports.retry", "Retry")}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className='content_wrap pt-5'>
          <h4 className='text-white fw-semibold mb-4'>{t("reports.relatedFields")}</h4>
          {loadingFields && <div className="text-muted mb-3">{t("reports.loadingRelatedFields")}</div>}
          {!loadingFields && (
            <div className="row g-4">
              {fields.map((field, index) => (
                <div className="col-md-6 col-lg-3" key={field.id || index}>
                  <div
                    className="reportPoster_img_wrap d-block"
                    role="button"
                    onClick={() =>
                      navigate(
                        `/reports/overview?sector_id=${field.id || ""}&sector_name=${encodeURIComponent(
                          field.name || ""
                        )}&sector_image=${encodeURIComponent(field.image_url || "")}`
                      )
                    }
                  >
                    <img
                      src={field.image_url || PLACEHOLDER_IMG}
                      alt={translateSectorLabel(field.name, lang) || t("reports.field")}
                      loading="lazy"
                      decoding="async"
                      className='h-100 w-100 object-fit-cover'
                    />
                    <div className="title">
                      <h4 className='text-white mb-0'>{translateSectorLabel(field.name, lang)}</h4>
                    </div>
                  </div>
                </div>
              ))}
              {fields.length === 0 && <div className="text-muted">{t("reports.noRelatedFields")}</div>}
            </div>
          )}
        </div>

        <div className="content_wrap pt-5 reports_souraces_tabs">
          <ul className="nav nav-pills mb-4 custom_tabs" id="pills-tab" role="tablist">
            <li className="nav-item" role="presentation">
              <button
                className="nav-link active"
                id="pills-home-tab"
                data-bs-toggle="pill"
                data-bs-target="#pills-home"
                type="button"
                role="tab"
                aria-controls="pills-home"
                aria-selected="true"
              >
                {t("reports.allSectors")}
              </button>
            </li>
            <li className="nav-item" role="presentation">
              <button
                className="nav-link"
                id="pills-profile-tab"
                data-bs-toggle="pill"
                data-bs-target="#pills-profile"
                type="button"
                role="tab"
                aria-controls="pills-profile"
                aria-selected="false"
              >
                {t("reports.allSources")}
              </button>
            </li>
          </ul>

          <div className="tab-content" id="pills-tabContent">
            {/* === Tab 1: All Sectors === */}
            <div
              className="tab-pane fade show active"
              id="pills-home"
              role="tabpanel"
              aria-labelledby="pills-home-tab"
              tabIndex="0"
            >
              {loadingSectors && <div className="text-muted mb-3">{t("reports.loadingSectors")}</div>}
              {!loadingSectors && (
                <div className="row g-4">
                  {visibleSectors.map((sector, index) => (
                    <div className="col-md-6 col-lg-3" key={sector.id || index}>
                      <div
                        className="reportPoster_img_wrap"
                        role="button"
                        onClick={() =>
                          navigate(
                            `/reports/overview?sector_id=${sector.id || ""}&sector_name=${encodeURIComponent(
                              sector.name || ""
                            )}&sector_image=${encodeURIComponent(sector.image_url || "")}`
                          )
                        }
                      >
                        <img
                          src={sector.image_url || PLACEHOLDER_IMG}
                          alt={translateSectorLabel(sector.name, lang) || t("reports.sector")}
                          loading="lazy"
                          decoding="async"
                          className="h-100 w-100 object-fit-cover"
                        />
                        <div className="title">
                          <h5 className="text-white mb-0">{translateSectorLabel(sector.name, lang)}</h5>
                          {typeof sector.report_count === 'number' && (
                            <p className="text-white-50 small mb-0">{t("reports.count", { count: sector.report_count })}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  {sectors.length === 0 && <div className="text-muted">{t("reports.noSectors")}</div>}
                </div>
              )}
              {!loadingSectors && sectors.length > visibleSectorsCount && (
                <div className="d-flex justify-content-center mt-4">
                  <button
                    type="button"
                    className="btn btn-outline-light px-4"
                    onClick={() =>
                      setVisibleSectorsCount((prev) => prev + SECTORS_PAGE_SIZE)
                    }
                  >
                    {t("reports.showMoreSectors")}
                  </button>
                </div>
              )}
            </div>

            {/* === Tab 2: All Sources === */}
            <div
              className="tab-pane fade"
              id="pills-profile"
              role="tabpanel"
              aria-labelledby="pills-profile-tab"
              tabIndex="0"
            >
              {loadingSources && <div className="text-muted mb-3">{t("reports.loadingSources")}</div>}
              {!loadingSources && (
                <div className="row g-4">
                  {sources.map((src, index) => (
                    <div className="col-md-6 col-lg-3" key={src.id || index}>
                      <div
                        className="reportPoster_img_wrap"
                        role="button"
                        onClick={() =>
                          navigate(
                            `/reports/overview?source_id=${src.id || ""}&source_name=${encodeURIComponent(
                              src.name || ""
                            )}&source_image=${encodeURIComponent(src.logo_url || "")}`
                          )
                        }
                      >
                        <img
                          src={proxiedLogoUrl(src.logo_url) || PLACEHOLDER_IMG}
                          alt={src.name || t("reports.source")}
                          onError={(e) => {
                            if (e.currentTarget.dataset.fallback) return;
                            e.currentTarget.dataset.fallback = "1";
                            e.currentTarget.src = PLACEHOLDER_IMG;
                          }}
                          loading="lazy"
                          decoding="async"
                          className="h-100 w-100 object-fit-cover"
                        />
                        <div className="title">
                          <h5 className="text-white mb-0" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {src.name || ''}
                          </h5>
                        </div>
                      </div>
                    </div>
                  ))}
                  {sources.length === 0 && <div className="text-muted">{t("reports.noSources")}</div>}
                </div>
              )}
            </div>
          </div>
        </div>


      </div>
    </div>
  );
};

export default ReportList;
